import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  LinearProgress, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

const TOUR_STEPS = [
  {
    title: 'Welcome to SoldiKeeper DMS 🎉',
    icon: '🚀',
    description: 'Your enterprise-grade document management system. Powered by AI, secured by design.',
    highlight: 'This quick tour will show you the key features in under 2 minutes.',
    color: '#4fc3f7',
  },
  {
    title: 'Your Document Vaults 🗄️',
    icon: '📁',
    description: 'Documents are organized into 9 specialized vaults: Procedures, Legal, Technical, Financial, Marketing, Contracts, Customer, HR, and Compliance.',
    highlight: 'Navigate vaults from the left sidebar. Each vault has its own access controls.',
    color: '#81c784',
  },
  {
    title: 'Upload & AI Classification 🤖',
    icon: '⬆️',
    description: 'Upload any document (PDF, Word, Excel, images) and our AI will automatically classify it, extract text, suggest tags, and generate a summary.',
    highlight: 'The AI works on upload — no extra steps needed.',
    color: '#ffb74d',
  },
  {
    title: 'AI-Powered Search 🔍',
    icon: '🧠',
    description: 'Search in natural language: "Find the GDPR compliance policy from last year" or "Contracts with Acme Corp". The AI understands your intent.',
    highlight: 'Use the search bar at the top to try AI search right now.',
    color: '#ce93d8',
  },
  {
    title: 'Security Classification 🔐',
    icon: '🛡️',
    description: 'Every document can be classified: Unclassified, Internal, Confidential, Restricted, or Secret. Color-coded badges make security levels instantly visible.',
    highlight: 'Set classification when uploading or editing any document.',
    color: '#ef9a9a',
  },
  {
    title: 'Approval Workflows ✅',
    icon: '📋',
    description: 'Submit documents through multi-step approval workflows. Get notified when action is required. Track the full approval history with timestamps.',
    highlight: 'Find Workflows in the left sidebar.',
    color: '#80cbc4',
  },
  {
    title: 'Professional Templates 📄',
    icon: '✨',
    description: 'Start with 10+ ready-to-use professional templates: NDAs, SOPs, Policies, Financial Reports, Incident Reports, and more. Customize with merge fields.',
    highlight: 'Browse Templates in the sidebar. Fill in the blanks and create instantly.',
    color: '#fff176',
  },
];

const STORAGE_KEY = 'dms-onboarding-complete';

const OnboardingTour = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      setTimeout(() => setOpen(true), 1000);
    }
  }, []);

  const handleClose = (permanent = false) => {
    setOpen(false);
    if (permanent) localStorage.setItem(STORAGE_KEY, '1');
  };

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      handleClose(true);
    }
  };

  const handleBack = () => setStep(s => Math.max(0, s - 1));

  const currentStep = TOUR_STEPS[step];
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `2px solid ${currentStep.color}40`,
          background: 'linear-gradient(135deg, rgba(13,33,55,0.98) 0%, rgba(10,25,41,0.99) 100%)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={() => handleClose(true)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            borderRadius: 1, height: 4, mb: 2,
            bgcolor: 'rgba(255,255,255,0.1)',
            '& .MuiLinearProgress-bar': { bgcolor: currentStep.color },
          }}
        />
        <Box sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '3rem', mb: 1 }}>{currentStep.icon}</Box>
          <Typography variant="h6" fontWeight={700}>{currentStep.title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
          {currentStep.description}
        </Typography>
        <Box sx={{ bgcolor: `${currentStep.color}15`, border: `1px solid ${currentStep.color}40`, borderRadius: 2, p: 1.5 }}>
          <Typography variant="body2" sx={{ color: currentStep.color, fontWeight: 600 }}>
            💡 {currentStep.highlight}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 3 }}>
          {TOUR_STEPS.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: idx === step ? 20 : 8, height: 8, borderRadius: 4,
                bgcolor: idx === step ? currentStep.color : 'rgba(255,255,255,0.2)',
                transition: 'all 0.3s',
                cursor: 'pointer',
              }}
              onClick={() => setStep(idx)}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button size="small" onClick={() => handleClose(true)} color="inherit" sx={{ opacity: 0.6 }}>
          Skip Tour
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {step > 0 && (
            <Button size="small" onClick={handleBack} startIcon={<NavigateBeforeIcon />} variant="outlined">Back</Button>
          )}
          <Button
            size="small"
            onClick={handleNext}
            variant="contained"
            endIcon={step < TOUR_STEPS.length - 1 ? <NavigateNextIcon /> : undefined}
            sx={{ bgcolor: currentStep.color, '&:hover': { bgcolor: currentStep.color, filter: 'brightness(0.9)' } }}
          >
            {step < TOUR_STEPS.length - 1 ? 'Next' : '🚀 Get Started'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export const OnboardingResetButton = () => {
  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };
  return <Button size="small" onClick={handleReset}>Restart Tour</Button>;
};

export default OnboardingTour;
